type ClientErrorSource =
  | "react-error-boundary"
  | "window.error"
  | "window.unhandledrejection"
  | "localStorage.parse";

interface ClientErrorLogParams {
  source: ClientErrorSource;
  error?: unknown;
  route?: string;
  extra?: Record<string, unknown>;
}

type ClientErrorTriggerType =
  | "window.error"
  | "window.unhandledrejection"
  | "localStorage.parse";

const CLIENT_ERROR_LOG_URL =
  import.meta.env.VITE_CLIENT_ERROR_LOG_URL ||
  (import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/log/client-error`
    : "/api/log/client-error");

const IGNORED_THIRD_PARTY_ERROR_PATTERNS = [
  "static.zdassets.com/ekr/snippet.js",
  "static.zdassets.com/ekr/sentry-browser.min.js",
  "loading chunk",
];

const IGNORED_BROWSER_ERROR_MESSAGES = [
  "resizeobserver loop completed with undelivered notifications",
  "resizeobserver loop limit exceeded",
];

const safeGetStorageValue = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const normalizeError = (
  error: unknown
): { message: string; stack?: string } => {
  if (error instanceof Error) {
    return { message: error.message || "Unknown error", stack: error.stack };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: "Unknown non-serializable error" };
  }
};

const shouldIgnoreClientError = ({
  message,
  stack,
  extra,
}: {
  message: string;
  stack?: string;
  extra?: Record<string, unknown>;
}): boolean => {
  const normalizedMessage = message.toLowerCase();
  const normalizedStack = stack?.toLowerCase() || "";
  const normalizedFilename =
    typeof extra?.filename === "string" ? extra.filename.toLowerCase() : "";

  if (
    IGNORED_BROWSER_ERROR_MESSAGES.some((ignoredMessage) =>
      normalizedMessage.includes(ignoredMessage)
    )
  ) {
    return true;
  }

  const hasZendeskReference =
    normalizedMessage.includes("static.zdassets.com") ||
    normalizedMessage.includes("snippet.js") ||
    normalizedMessage.includes("sentry-browser.min.js") ||
    normalizedStack.includes("static.zdassets.com") ||
    normalizedFilename.includes("static.zdassets.com");

  if (!hasZendeskReference) {
    return false;
  }

  return IGNORED_THIRD_PARTY_ERROR_PATTERNS.some((pattern) => {
    const normalizedPattern = pattern.toLowerCase();
    return (
      normalizedMessage.includes(normalizedPattern) ||
      normalizedStack.includes(normalizedPattern) ||
      normalizedFilename.includes(normalizedPattern)
    );
  });
};

export const logClientError = async ({
  source,
  error,
  route,
  extra,
}: ClientErrorLogParams): Promise<void> => {
  try {
    const normalized = normalizeError(error);

    if (
      shouldIgnoreClientError({
        message: normalized.message,
        stack: normalized.stack,
        extra,
      })
    ) {
      return;
    }

    const payload = {
      timestamp: new Date().toISOString(),
      source,
      message: normalized.message,
      stack: normalized.stack,
      url: window.location.href,
      route: route || `${window.location.pathname}${window.location.search}`,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      username: safeGetStorageValue("username"),
      userId: safeGetStorageValue("userId"),
      extra,
    };

    const body = JSON.stringify(payload);

    if (typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(CLIENT_ERROR_LOG_URL, blob);
      return;
    }

    await fetch(CLIENT_ERROR_LOG_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    });
  } catch (logError) {
    console.error("Failed to report client error", logError);
  }
};

export const triggerClientErrorForTest = (
  type: ClientErrorTriggerType,
  options?: { applicationId?: string }
): void => {
  if (type === "window.error") {
    setTimeout(() => {
      throw new Error("manual-window-error-test");
    }, 0);
    return;
  }

  if (type === "window.unhandledrejection") {
    Promise.reject(new Error("manual-unhandledrejection-test"));
    return;
  }

  const applicationId = options?.applicationId || "35331";
  localStorage.setItem("openGlobalTabs", "{bad-json");
  window.location.assign(`/applicationSummary/${applicationId}`);
};
